import {
  BARREN_HOUSING,
  BUILT_HOUSING,
  DEFAULT_SPEC_VALUE,
  DEFAULT_HORSE_VALUE,
  rankModifiers,
  getRankLevel,
  DEFAULT_ELITE_VALUE,
  DEFAULT_PRISONER_VALUE,
  DEFAULT_MOD,
  buildingEffects,
  PRISONERS_JOB,
  TOWER_RUNE,
  FARMS_BUSHEL,
  BARREN_BUSHEL,
  FL_FOOD_MOD,
  BANKS_GC_RAW,
  EMPLOYED_GC,
  UNEMPLOYED_GC,
  PRISONER_GC,
  SPEC_WAGE,
  ELITE_WAGE,
  HI_WAGE_MOD,
  IA_WAGE_MOD,
  GREED_WAGE_MOD,
  FAN_OME_MOD,
  BL_OME_MOD,
  FAN_DME_MOD,
  MP_DME_MOD,
  GP_DME_MOD,
  DEFAULT_SOLD_DEF_VALUE,
} from './GameAgeData';
import { removeDecimalsToNumber } from './NumberFormatting';
import {
  ArmyCalc,
  BuildingEffectType,
  CalcEnemy,
  CalcProv,
  CalculateBeProps,
  CalculateIncome,
  CalculateWages,
  CalculatedEconomy,
  LivingSpaceProps,
  RankModifier,
  Ritual,
  RitualModifier,
  Title,
} from '../@types/Calculations';
import { ArmiesAway, Som, Sot } from '../@types/KingdomResponse';
import { KnownAttrNames, RacePersona } from '../@types/RacesResponse';
import { IntelState } from '../@types/State';
import { STORAGE_KEY, parseState } from '../context/IntelState';

export const calculateBE = ({
  acres,
  numberHomes,
  modLivingSpace,
  armyCalc,
  toolsMod,
  ritBeMod,
}: CalculateBeProps) => {
  const availableJobs = (acres - numberHomes) * BUILT_HOUSING;
  const optimalWorkers = availableJobs * 0.67;
  const peasants =
    modLivingSpace - armyCalc.total - armyCalc.wizards - armyCalc.thieves;
  const availableWorkers =
    peasants + Math.floor(armyCalc.prisoners / PRISONERS_JOB);
  const unfilledJobs = Math.max(availableJobs - availableWorkers, 0);
  const employed = Math.min(availableJobs, availableWorkers);
  const unemployed = Math.max(
    peasants - availableJobs - Math.floor(armyCalc.prisoners / PRISONERS_JOB),
    0
  );
  const jobsPerformedPcnt = Math.min(availableWorkers / optimalWorkers, 1);
  const roundedJobsPerformed = Math.floor(jobsPerformedPcnt * 10000) / 100;

  const estBE =
    0.5 *
    (1 + jobsPerformedPcnt) *
    DEFAULT_MOD * // TODO race combo BE mod
    toolsMod *
    ritBeMod;
  const currentDraft =
    Math.floor(
      ((armyCalc.total + armyCalc.thieves) / modLivingSpace) * 100 * 100
    ) / 100;
  return {
    estBE,
    unfilledJobs,
    employed,
    unemployed,
    peasants,
    jobsPerformedPcnt: roundedJobsPerformed,
    availableJobs,
    availableWorkers,
    optimalWorkers,
    currentDraft,
  } as CalculatedEconomy;
};

export const calculateRunesPerTick = (
  numberTowers: number,
  productionMod: number
) => {
  return numberTowers * TOWER_RUNE * productionMod;
};

export const calculateBushelsPerTick = (
  acres: number,
  numberFarms: number,
  numberBarren: number,
  be: number,
  productionMod: number,
  fertileLands: boolean
) => {
  const raceModFood = (1 * acres) / 2; // TODO ??? what is this?
  const baseFoodProduction =
    numberFarms * FARMS_BUSHEL * be +
    numberBarren * BARREN_BUSHEL +
    raceModFood;
  const modFoodProduction =
    baseFoodProduction *
    productionMod *
    (fertileLands ? FL_FOOD_MOD : DEFAULT_MOD);
  return modFoodProduction;
};

export const getBanksBonusGc = (numberBanks: number, be: number) => {
  return Math.floor(numberBanks * BANKS_GC_RAW * be);
};

export const calculateModIncome = ({
  employed,
  unemployed,
  prisoners,
  banksBonusGc,
  banksIncomeModifier,
  alchemyMod,
  rankIncomeModifier,
  raceIncomeMod,
  ritIncomeMod,
}: CalculateIncome) => {
  const rawIncome =
    EMPLOYED_GC * employed +
    UNEMPLOYED_GC * unemployed +
    PRISONER_GC * prisoners +
    banksBonusGc;

  const modIncomeBeforeRounding =
    rawIncome *
    banksIncomeModifier *
    alchemyMod *
    rankIncomeModifier *
    raceIncomeMod *
    ritIncomeMod;

  const modIncome = Math.floor(modIncomeBeforeRounding);
  return { modIncome, rawIncome };
};

export const calculateWages = ({
  wagesPcnt,
  armyCalc,
  hasHI,
  hasIA,
  hasGreed,
  armsWagesModifier,
  ritWagesMod,
  bookkeepingMod,
}: CalculateWages) => {
  const wageRate = wagesPcnt / 100;
  const baseWages = Math.floor(
    (armyCalc.dSpecs + armyCalc.oSpecs) * SPEC_WAGE +
      armyCalc.elites * ELITE_WAGE
  );
  const wagesSpellMod = hasHI ? HI_WAGE_MOD : hasIA ? IA_WAGE_MOD : DEFAULT_MOD;

  const wages =
    baseWages *
    wageRate *
    armsWagesModifier *
    wagesSpellMod *
    ritWagesMod *
    (hasGreed ? GREED_WAGE_MOD : DEFAULT_MOD) *
    bookkeepingMod;

  return Math.floor(wages);
};

export const calcQuadratic = (x: number, a: number, b: number, c = 0) => {
  const r = a * x * x + b * x + c;
  return r;
};

export const getDiminishedBuildingEffect = (
  pct: number,
  totalAcres: number,
  type: BuildingEffectType,
  be: number
) => {
  const numberBuilding = (totalAcres * pct) / 100;
  const effectType = buildingEffects[type];
  const limit = effectType.limit;
  const scale = effectType.scale * (effectType.be ? be : 1);
  const effect = getDimishingEffect(numberBuilding, totalAcres, scale, limit);
  return effect;
};

export const getDiminishedBuildingMod = (
  pct: number,
  totalAcres: number,
  type: BuildingEffectType,
  be: number,
  direction: '+' | '-'
) => {
  const effect =
    Math.round(getDiminishedBuildingEffect(pct, totalAcres, type, be) * 10000) /
    10000;
  return direction === '+' ? 1 + effect : 1 - effect;
};

export const getDimishingEffect = (
  land: number,
  totalLand: number,
  scale: number,
  limit: number
) => {
  // fraction of relevant land type out of total land, capped at 0.5
  // Caps 'X' feeding into the function which in turn obviously caps Y.
  let landFraction = 0;
  if (totalLand > 0) {
    landFraction = Math.min(0.5, land / totalLand);
  } else {
    landFraction = 0.5;
  }

  // This is of the form (1x - 1x^2), which is an n-shaped
  // quadratic, with a root at 0 and max y at 0.25.
  // Since land_fraction 'x' is capped, this expression becomes constant
  // when land_fraction is greater than 0.5.
  const a = -1;
  const b = 1;
  let y = calcQuadratic(landFraction, a, b);

  // The maximum value of the quadratic (x - x^2) is 0.25. Multiply it by
  // 4 to put it in the range 0-1. Now we have an N shaped curve with
  // y values of [0, 1]
  y *= 4;

  // The output of each effect needs to be on a suitable scale for
  // whatever it contributes to.
  y *= scale;

  // Each effect also has a max upper limit. Cap 'y' regardless of the 'x' cap above.
  return Math.min(limit, y);
};

export const getWarGains = (target: CalcEnemy, province: CalcProv) => {
  const getMap = (mapText: string | undefined) => {
    if (!mapText) return 1;
    if (mapText.includes('Not')) {
      return 1;
    } else if (mapText.includes('little')) {
      return 0.9;
    } else {
      return 0.8;
    }
  };

  const getPnf = (nwRatio: number) => {
    let pnf;
    if (nwRatio < 0.567) {
      pnf = 0;
    } else if (nwRatio < 0.9) {
      pnf = 3 * nwRatio - 1.7;
    } else if (nwRatio < 1.1) {
      pnf = 1;
    } else if (nwRatio < 1.6) {
      pnf = -2 * nwRatio + 3.2;
    } else {
      pnf = 0;
    }
    return pnf;
  };

  const minGainInWar = 0.04;
  const warGain = 0.12;
  const nwRatio = target.networth ? target.networth / province.networth : 1;
  const pnf = getPnf(nwRatio);
  const map = getMap(target.map);
  const gainPcntMod = Math.max(
    warGain * pnf * map * province.siege,
    minGainInWar
  );
  const landGain = removeDecimalsToNumber(target.land * gainPcntMod, 0);
  const gainPctRounded = removeDecimalsToNumber(gainPcntMod * 100, 1);

  return {
    landGain,
    gainPctRounded,
  };
};

export const getRacePersonaFromStorage = (
  race: string,
  personality: string | undefined | null
): RacePersona | undefined => {
  const intelState: IntelState = parseState(localStorage.getItem(STORAGE_KEY));
  const races =
    intelState.server === 1 ? intelState.racesWol : intelState.racesGen;
  const racePersona: RacePersona | undefined = races?.find(
    (x) => x.race === race && x.personality === personality
  );
  return racePersona;
};

export const getAttrMod = (
  racePersona: RacePersona | undefined,
  key: KnownAttrNames,
  defaultValue = 0
): number => {
  const keyExists: string | number | undefined = racePersona?.attrs[key];
  return keyExists ? (keyExists as number) : defaultValue;
};

export const getRaceComboHonorMod = (
  rankModifier: RankModifier,
  title: Title,
  racePersona?: RacePersona
): number => {
  let rankLevel: number = getRankLevel(title);
  if (rankLevel > 4) {
    rankLevel += rankLevel - 4;
  }
  const baseModifier: number = rankModifiers[rankModifier];
  const raceComboHonorMod = racePersona
    ? getAttrMod(racePersona, 'HonorMod', 1)
    : 1;
  return 1 + baseModifier * rankLevel * raceComboHonorMod;
};

export const getLivingSpace = ({
  homesBonus,
  sciMod,
  built,
  barrenCount,
  title,
  racePersona,
}: LivingSpaceProps) => {
  const raceMod = getAttrMod(racePersona, 'PopulationMod', 1);
  const honorMod = getRaceComboHonorMod('population', title, racePersona);
  const rawLivingSpace =
    built * BUILT_HOUSING + barrenCount * BARREN_HOUSING + homesBonus;
  const modLivingSpace = Math.round(
    rawLivingSpace * raceMod * sciMod * honorMod
  );
  return { modLivingSpace, rawLivingSpace };
};

export const getRitualModInc = (
  property: RitualModifier,
  ritual: Ritual,
  ritEff: number
) => {
  if (ritual.key > 0) {
    const value = ritual[property] * 100 - 100;
    const modValue = value * (ritEff / 100);
    const modPct = (100 + modValue) / 100;
    return modPct;
  } else {
    return 1;
  }
};

export const getRitualModDec = (
  property: RitualModifier,
  ritual: Ritual,
  ritEff: number
) => {
  if (ritual.key > 0) {
    const value = ritual[property] * 100 - 100;
    const modValue = value * (ritEff / 100);
    return (100 - modValue) / 100;
  } else {
    return 1;
  }
};

export const getCurrentDraftLevelFactor = (armyPercentageOfMaxPop: number) => {
  return (
    //* TODO base rate for level ?? What does this mean?
    (Math.max(
      1.0154 * Math.pow(armyPercentageOfMaxPop, 2) +
        1.1759 * armyPercentageOfMaxPop +
        0.3633,
      1
    ) * 1)
  );
};

export const getBaseME = (wages: number) =>
  33 + 67 * Math.pow(wages / 100, 0.25); // TODO not including  Ruby and GBP

export const getOME = (
  baseME: number,
  trainingGroundsOMEBonus: number,
  rankOmeMod: number,
  tacticsMod: number,
  raceOmeMod: number,
  ritOmeMod: number,
  useFan: boolean | undefined,
  useBl: boolean | undefined
) => {
  const ome =
    (baseME + trainingGroundsOMEBonus + (rankOmeMod - 1) * 100) *
    tacticsMod *
    raceOmeMod *
    (useFan ? FAN_OME_MOD : DEFAULT_MOD) *
    (useBl ? BL_OME_MOD : DEFAULT_MOD) *
    ritOmeMod;
  return ome;
};

export const getDME = (
  baseME: number,
  fortsDMEBonus: number,
  strategyMod: number,
  raceDmeMod: number,
  ritDmeMod: number,
  useFan: boolean | undefined,
  useMp: boolean | undefined,
  useGp: boolean | undefined
) => {
  const dME =
    (baseME + fortsDMEBonus) *
    strategyMod *
    raceDmeMod *
    (useFan ? FAN_DME_MOD : DEFAULT_MOD) *
    (useMp ? MP_DME_MOD : DEFAULT_MOD) *
    (useGp ? GP_DME_MOD : DEFAULT_MOD) *
    ritDmeMod;
  return dME;
};

export const getConstructionCost = (
  acres: number,
  millsMod: number,
  ritualMod = 1,
  artisanModifier = 1
) => {
  const constructionRaceMod = DEFAULT_MOD; // TODO get raceCombo mod
  return Math.floor(
    Math.floor(0.05 * (acres + 10000)) *
      constructionRaceMod *
      millsMod *
      ritualMod *
      artisanModifier
  );
};

export const getRazeCost = (acres: number, artisanModifier = 1) =>
  Math.floor((300 + 0.05 * acres) * artisanModifier);

export const getModOff = (rawOff: number, oME: number) =>
  Math.floor(rawOff * (oME / 100 + 0)); // not inc generals

export const getModDef = (rawDef: number, dME: number, acres: number) =>
  Math.floor(Math.max(rawDef * ((dME / 100) * 1), acres));

export const getRawOff = (
  racePersona: RacePersona | undefined,
  armyCalc: ArmyCalc,
  incAgr: boolean
) => {
  const soldOffRaw = getAttrMod(racePersona, 'SoldierOff', 1);
  const offSpecOffRaw = getAttrMod(
    racePersona,
    'OffSpecOff',
    DEFAULT_SPEC_VALUE
  );
  const eliteOffRaw = getAttrMod(racePersona, 'EliteOff', DEFAULT_ELITE_VALUE);
  const horseOffRaw = getAttrMod(racePersona, 'HorseOff', DEFAULT_HORSE_VALUE);
  const prisonerOffRaw = getAttrMod(
    racePersona,
    'PrisonerOff',
    DEFAULT_PRISONER_VALUE
  );
  return (
    armyCalc.soldiers * (soldOffRaw + (incAgr ? 2 : 0)) +
    armyCalc.oSpecs * offSpecOffRaw +
    armyCalc.elites * eliteOffRaw +
    armyCalc.horses * horseOffRaw +
    armyCalc.prisoners * prisonerOffRaw
  );
};

export const getRawDef = (
  racePersona: RacePersona | undefined,
  armyCalc: ArmyCalc,
  incAgr: boolean
) => {
  const soldDefRaw = getAttrMod(
    racePersona,
    'SoldierDef',
    DEFAULT_SOLD_DEF_VALUE
  );
  const defSpecDefRaw = getAttrMod(
    racePersona,
    'DefSpecDef',
    DEFAULT_SPEC_VALUE
  );
  const eliteDefRaw = getAttrMod(racePersona, 'EliteDef', DEFAULT_ELITE_VALUE);
  return (
    armyCalc.soldiers * (soldDefRaw - (incAgr ? 2 : 0)) +
    armyCalc.dSpecs * defSpecDefRaw +
    armyCalc.elites * eliteDefRaw
  );
};

export const getArmyCalcHome = (sot: Sot, som: Som) => {
  // experimental
  const armyCalc: ArmyCalc = {
    soldiers: som.standingArmy.solds,
    soldiersTraining: 0,
    dSpecs: sot.dSpecs,
    oSpecs: som.standingArmy.oSpecs,
    elites: som.standingArmy.elites,
    horses: som.standingArmy.horses,
    thieves: sot.thieves,
    prisoners: sot.prisoners,
    wizards: sot.wizards,
    total: 0,
  };
  if (sot.ageSeconds < som.ageSeconds) {
    const elitesAway = som.armiesAway.reduce(
      (accumulator, currentValue: ArmiesAway) =>
        accumulator + currentValue.elites,
      0
    );
    const soldsAway = som.armiesAway.reduce(
      (accumulator, currentValue: ArmiesAway) =>
        accumulator + currentValue.solds,
      0
    );
    armyCalc.elites = sot.elites - elitesAway;
    armyCalc.soldiers = sot.soldiers - soldsAway;
    armyCalc.oSpecs = sot.oSpecs;
    armyCalc.horses = sot.horses;
    return armyCalc;
  }
  return armyCalc;
};
